"use client";
import { type JSX, useEffect, useState } from "react";
import { Button, ButtonProps } from "@cappelendamm/design-system/react";
import { useCart } from "@/cart";
import classNames from "classnames";

import { PiCircleNotch } from "react-icons/pi";
import { isItemDigital } from "@/products/selectors/isItemDigital";
import { getAddToCartButtonState } from "@/utils/getAddToCartButtonState";
import { useFeidePurchaseGate } from "@/products/components/useFeidePurchaseGate";

export type AddToCartButtonProps = {
  sku: string;
  canBeSold: boolean;
  statusMessage?: string;
  publicationDate?: string;
  iconOnly?: boolean;
  requiresFeideUser?: boolean;
};

export function AddToCartButton({
  children = "Legg i handlekurv",
  onClick,
  sku,
  className,
  canBeSold,
  statusMessage,
  publicationDate,
  iconOnly = false,
  requiresFeideUser = false,
  ...rest
}: AddToCartButtonProps & ButtonProps & JSX.IntrinsicElements["button"]) {
  const [isSubmittingCurrentButton, setIsSubmittingCurrentButton] =
    useState(false);

  const { addItem, isPending, cart, cartDisabled } = useCart();
  const { isFeidePurchaseBlocked, isFeideAuthLoading } =
    useFeidePurchaseGate(requiresFeideUser);

  const currentItem = cart?.items?.find((item) => item.variant?.sku === sku);

  const isDigital = isItemDigital(currentItem);

  const { disabled: isUnavailable, label } = getAddToCartButtonState(
    canBeSold,
    statusMessage,
    publicationDate,
  );
  const disabled = isUnavailable || isFeidePurchaseBlocked;

  const isCurrentButtonPending = isSubmittingCurrentButton;
  const isButtonDisabled =
    isPending ||
    isCurrentButtonPending ||
    disabled ||
    isDigital ||
    isFeideAuthLoading;

  useEffect(() => {
    if (!isPending && isSubmittingCurrentButton) {
      setIsSubmittingCurrentButton(false);
    }
  }, [isPending, isSubmittingCurrentButton]);

  if (cartDisabled) return null;

  return (
    <Button
      {...rest}
      className={classNames(className, "relative", {
        "whitespace-nowrap": !iconOnly,
        "disabled:bg-(--cvr-button-background) disabled:text-(--cvr-button-contrast)":
          Boolean(cart) && isCurrentButtonPending,
        "cursor-not-allowed": Boolean(disabled) && !isPending,
      })}
      disabled={isButtonDisabled || undefined}
      aria-busy={isCurrentButtonPending || undefined}
      onClick={async (event) => {
        if (isButtonDisabled) {
          event.preventDefault();
          return;
        }
        setIsSubmittingCurrentButton(true);
        try {
          await addItem({ sku });
        } catch {
          setIsSubmittingCurrentButton(false);
        }
        if (onClick) onClick(event);
      }}
    >
      {isCurrentButtonPending && Boolean(cart) ? (
        <>
          <span className="opacity-0">{children}</span>
          <PiCircleNotch className="pointer-events-none absolute inset-0 m-auto animate-spin" />
        </>
      ) : !iconOnly && label ? (
        label
      ) : disabled && !iconOnly ? (
        "Ikke tilgjengelig"
      ) : (
        children
      )}
    </Button>
  );
}
